import React, { useMemo } from 'react';
import { Line } from '@react-three/drei';

import { TrussEnvelope } from '@paragon/contract-library';
import { Line2D, LineSegment2D } from '@paragon/geometry-library';
import { calculatePlyDividerSegments, getEnvelopeSegmentWithOverhangsFromEnvelope } from '@paragon/layout';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { convertFromLayoutToDesignCoordinates } from '../../../library/layout-geometry/coordinates';
import { ManualRenderModes } from '../../../library/renderModes';
import { EnvelopeRenderMode } from '../../../store/slices/visualOptionsSlice';
import { LAYOUT_MEMBER_EDGE_COLOR } from '../../component/MemberVisual';
import { EnvelopePlyDividers } from '../EnvelopePlyDividers';
import { layoutColors } from '../layoutColors';
import { Segment } from '../Segments';
import { getEnvelopeColor, getOverhangEnvelopeSegmentColor, TrussEnvelopeStyle } from './colors';
import { NEVER_RAYCAST } from './TrussEnvelopeVisual';

function getDefinitionLineColor(style: TrussEnvelopeStyle) {
  switch (style) {
    case 'emphasised':
      return layoutColors.trussEnvelopes.emphasised.definitionLine;

    case 'faded':
      return layoutColors.trussEnvelopes.faded.definitionLine;

    case 'normal':
      return layoutColors.trussEnvelopes.normal.definitionLine;

    case 'highlighted':
      return layoutColors.trussEnvelopes.highlighted.definitionLine;

    default:
      assertExhaustiveSwitch(style);
  }
}

export function TrussEnvelopeEdgeVisuals(props: {
  envelope: TrussEnvelope;
  renderMode: ManualRenderModes<EnvelopeRenderMode>;

  in3D: boolean;
  style: TrussEnvelopeStyle;

  showPlyDividers: boolean;

  envelopeSegments: readonly LineSegment2D[];
  overhangSegments: readonly LineSegment2D[];
}) {
  const definitionLine2D = Line2D.fromPointObjects(props.envelope.leftPoint, props.envelope.rightPoint);

  const definitionSegment = getEnvelopeSegmentWithOverhangsFromEnvelope(
    props.envelope,
    props.envelope.justification,
  )!.to3D(props.envelope.elevation);

  const definitionLineLeftPoint = convertFromLayoutToDesignCoordinates(definitionSegment.basePoint).toArray();
  const definitionLineRightPoint = convertFromLayoutToDesignCoordinates(definitionSegment.endPoint).toArray();

  const edgeSegmentColor = getEnvelopeColor(props.style);
  const overhangSegmentColor = getOverhangEnvelopeSegmentColor(props.style);

  const definitionSegmentColor = getDefinitionLineColor(props.style);

  const plyDividerSegments = useMemo(
    () => (props.showPlyDividers ? calculatePlyDividerSegments(props.envelope) : []),
    [props.showPlyDividers, props.envelope],
  );

  // center definition lines are rendered dashed, so they can't be lumped with the instanced lines
  const renderDefinitionLineSeparately = props.style === 'emphasised' && props.envelope.justification === 'Center';

  const renderingOverComponentDesign = props.envelope.componentDesignGuid != null && props.renderMode === 'components';

  return (
    <group raycast={NEVER_RAYCAST}>
      {props.envelopeSegments.map((edge, i) => {
        const edgeLine2D = Line2D.fromPointObjects(edge.basePoint, edge.endPoint);
        const isDefinitionSegment = edgeLine2D.equals(definitionLine2D);

        if (isDefinitionSegment && renderDefinitionLineSeparately) return null;

        const basePoint = convertFromLayoutToDesignCoordinates(edge.basePoint.to3D(props.envelope.elevation)).toArray();
        const endPoint = convertFromLayoutToDesignCoordinates(edge.endPoint.to3D(props.envelope.elevation)).toArray();

        const color = renderingOverComponentDesign
          ? LAYOUT_MEMBER_EDGE_COLOR
          : isDefinitionSegment
            ? definitionSegmentColor
            : edgeSegmentColor;

        return <Segment key={i} start={basePoint} end={endPoint} color={color} />;
      })}

      {props.overhangSegments.map((edge, i) => {
        const basePoint = convertFromLayoutToDesignCoordinates(edge.basePoint.to3D(props.envelope.elevation)).toArray();
        const endPoint = convertFromLayoutToDesignCoordinates(edge.endPoint.to3D(props.envelope.elevation)).toArray();

        return <Segment key={i} start={basePoint} end={endPoint} color={overhangSegmentColor} />;
      })}

      <EnvelopePlyDividers
        segments={plyDividerSegments}
        height={props.envelope.elevation}
        color={renderingOverComponentDesign ? LAYOUT_MEMBER_EDGE_COLOR : edgeSegmentColor}
      />

      {renderDefinitionLineSeparately && (
        <Line
          dashed
          depthTest={props.in3D}
          depthWrite={props.in3D}
          key="emphasised-center-definition-line"
          points={[definitionLineLeftPoint, definitionLineRightPoint]}
          color={definitionSegmentColor}
          raycast={NEVER_RAYCAST} // truss envelopes have a custom raycasting implementation
        />
      )}
    </group>
  );
}
